/* The support-chat bubble, and the "Support chat" section's buttons (sections/chat-help.liquid).
 *
 * Every element marked data-chat-launcher opens the same chat. The floating bubble is the one without
 * data-chat-inline; a section's button sits in the page and is marked data-chat-inline. One loader
 * serves them all, whichever is tapped first, and the script runs once however many copies of its tag
 * the page carries (the layout prints one for the bubble, each chat section one of its own).
 *
 * Nothing of Gorgias's is fetched until the shopper asks for it: the first tap injects the same
 * chat loader tag the app embed would have installed, waits for the widget, and opens it; a tap
 * whose loader fails, or never arrives within eight seconds, follows the bubble's link instead. The
 * bubble is a real link in the markup, so with this script absent or blocked the shopper still
 * reaches a human. Every vendor address comes from the snippet's data attributes — this file
 * contains none, and it registers no page-lifecycle listeners that would forbid the back cache.
 * While Shopify's privacy banner covers the bubble's corner, the bubble waits just above it.
 */
(() => {
  const WAIT_CAP = 30000     /* how long we look for window.GorgiasChat after the loader goes in */
  const LOADER_CAP = 8000    /* a tap whose loader has not arrived by then follows the bubble's own link */
  const OPEN_CAP = 30000     /* once the loader has run, how long a tap waits for the chat to open */

  /* Shopify's privacy banner, measured 2026-09-23 on a 390 x 844 phone: `section#shopify-pc__banner`,
     fixed across the bottom of the screen (351 x 363 px, z-index 2,000,000), added to the body about
     0.75 s after the page and switched to display:none once the shopper answers. Until then it sat on
     this bubble and took every tap. While a drawn banner covers the bubble's own corner, the bubble
     waits 16 px above the banner's top edge (never over its buttons, never under the site header or
     off the screen: with no room it stays hidden until the banner closes) and drops back the moment
     the banner goes. It moves by a transform, never by `bottom`: Chrome counts a change of `bottom`
     as the page jumping (0.028 on the phone collection page, measured 2026-09-23) and a transform not
     at all, and the insets and the size stay the stylesheet's. With no banner on the page nothing here
     writes a single style. */
  const BANNER_ID = 'shopify-pc__banner'
  const GAP = 16
  const ARRIVAL_CAP = 60000

  const clearOfBanner = (bubble) => {
    let banner = null      /* the banner being followed */
    let answered = false   /* the shopper answered while the banner was still drawn */
    let watcher = null     /* the banner's style and class, and its parent's children */
    let sizer = null       /* the banner's own box */
    let arrival = null     /* the body, until the banner arrives */

    const drawn = () => {
      if (!banner || !banner.isConnected) return null
      const style = getComputedStyle(banner)
      if (style.display === 'none' || style.visibility === 'hidden') return null
      const box = banner.getBoundingClientRect()
      return box.width > 0 && box.height > 0 ? box : null
    }

    /* The highest the lifted bubble may reach. The site header paints over it (z-index 20 against 15):
       measured on a phone held sideways (844 x 390), the lift put the bubble at y 103-159 under a header
       covering y 0-144, and a finger there reached the header's navigation. A header that scrolls with
       the page covers the top of the screen whenever the shopper is back at the top, so its place in the
       page counts; a fixed or sticky one covers where it is now. Never less than 16 px. */
    const ceiling = () => {
      let top = GAP
      try {
        const header = document.querySelector('.site-header') || document.querySelector('header')
        const box = header ? header.getBoundingClientRect() : null
        if (!box || !box.height) return top
        let pinned = false
        for (let el = header; el && el !== document.body; el = el.parentElement) {
          const position = getComputedStyle(el).position
          if (position === 'fixed' || position === 'sticky') { pinned = true; break }
        }
        top = Math.max(top, pinned ? box.bottom : box.bottom + (window.scrollY || 0))
      } catch (error) {}
      return top
    }

    /* Back to the stylesheet's own corner, safe area included. Writes only what is actually set. */
    const settle = () => {
      if (bubble.style.transform) bubble.style.removeProperty('transform')
      if (bubble.style.visibility) bubble.style.removeProperty('visibility')
    }

    const place = () => {
      try {
        if (bubble.hidden) { settle(); return stop() }
        if (banner && !banner.isConnected) return drop()
        const box = drawn()
        if (!box) answered = false
        settle()
        if (!box || answered) return
        const own = bubble.getBoundingClientRect()
        if (!own.width || !own.height) return
        if (own.right <= box.left || own.left >= box.right || own.bottom <= box.top || own.top >= box.bottom) return
        /* The banner's top edge, or higher if one of its controls reaches above it in our column. */
        let edge = box.top
        for (const control of banner.querySelectorAll('a, button, [role="button"]')) {
          const r = control.getBoundingClientRect()
          if (r.width && r.height && r.left < own.right && r.right > own.left) edge = Math.min(edge, r.top)
        }
        const floor = edge - GAP   /* where the bubble's bottom edge goes */
        if (floor - own.height < ceiling()) { bubble.style.visibility = 'hidden'; return }
        /* settle() above took any earlier lift off, so `own` is the bubble in its own corner */
        bubble.style.transform = 'translateY(' + (floor - own.bottom) + 'px)'
      } catch (error) {}
    }

    /* The storefront fires this the moment the shopper answers, while the banner is still drawn;
       the banner switches itself off a millisecond later (measured). */
    const answer = () => {
      try { answered = !!drawn() } catch (error) {}
      place()
    }

    const drop = () => {
      try { if (watcher) watcher.disconnect() } catch (error) {}
      try { if (sizer) sizer.disconnect() } catch (error) {}
      window.removeEventListener('resize', place)
      document.removeEventListener('visitorConsentCollected', answer)
      watcher = sizer = banner = null
      answered = false
      settle()
    }

    const stop = () => {
      try { if (arrival) arrival.disconnect() } catch (error) {}
      arrival = null
      drop()
    }

    const follow = (element) => {
      banner = element
      watcher = new MutationObserver((records) => {
        try {
          if (!banner) return
          if (!banner.isConnected) return drop()
          if (records.some(record => record.type === 'attributes')) place()
        } catch (error) {}
      })
      watcher.observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] })
      if (banner.parentNode) watcher.observe(banner.parentNode, { childList: true })
      if (typeof ResizeObserver === 'function') { sizer = new ResizeObserver(() => place()); sizer.observe(banner) }
      window.addEventListener('resize', place)
      document.addEventListener('visitorConsentCollected', answer)
      place()
    }

    const present = document.getElementById(BANNER_ID)
    if (present) return follow(present)
    /* Not on the page yet: watch the body for it, and stop watching once it is here or after 60 s. */
    arrival = new MutationObserver(() => {
      const element = document.getElementById(BANNER_ID)
      if (!element || !arrival) return
      arrival.disconnect()
      arrival = null
      try { follow(element) } catch (error) {}
    })
    arrival.observe(document.body || document.documentElement, { childList: true, subtree: true })
    setTimeout(() => { if (arrival) { arrival.disconnect(); arrival = null } }, ARRIVAL_CAP)
  }

  const boot = () => {
    if (window.__voissonChat) return
    const launchers = Array.from(document.querySelectorAll('[data-chat-launcher]'))
    if (!launchers.length) return
    window.__voissonChat = true
    /* the floating bubble, if the theme setting printed one; null on a page with only chat sections */
    const bubble = launchers.find(el => !el.hasAttribute('data-chat-inline')) || null
    const lead = bubble || launchers[0]   /* whose addresses and delay count, and who spins for a gs_open_chat link */

    const CHAT_SRC = lead.getAttribute('data-chat-src') || ''
    const CONVERT_SRC = lead.getAttribute('data-chat-convert-src') || ''
    const DELAY = Math.min(60, Math.max(0, parseInt(lead.getAttribute('data-chat-delay') || '0', 10) || 0))

    /* Measured 2026-09-23: with Gorgias's app embed switched on, its bundle needs 1-3 s to mount
       #gorgias-chat-container. Waiting for that container let our bubble show and steal the first
       tap, so we look for the embed's own loader TAG, which is in the markup from the first byte. */
    /* With the embed on, the bubble steps aside for theirs; a section's button stays and opens their chat.
       The tap listener still goes in, so a section the theme editor adds later works too, but nothing
       else of ours runs: their embed owns the delay and the gs_open_chat link. */
    const embedded = !!document.querySelector('script[data-gorgias-loader-chat], #gorgias-chat-container, iframe[title="Gorgias live chat messenger"]')
    if (embedded && bubble) bubble.hidden = true

    let started = false   /* the loaders go in at most once */
    let loaded = false    /* the chat loader has run: its load event fired */
    let broken = false    /* the loader failed, or a tap gave up: the next tap follows the link */
    let ready = null      /* settles once Gorgias's API may be used */
    let startChat = () => false   /* calls GorgiasChat.init() once GorgiasChat exists */
    let request = null    /* the tap or gs_open_chat link now waiting for the chat */

    const tag = (src, flag) => {
      if (!src || document.querySelector('script[' + flag + ']')) return null
      const script = document.createElement('script')
      script.defer = true
      script.async = false   /* a created script is async by default; the vendor's own tag is not */
      script.setAttribute(flag, '')
      script.src = src
      document.head.appendChild(script)
      return script
    }

    /* Until it is ready, window.GorgiasChat is a proxy that throws on every property except init()
       (measured 2026-09-23: "You are trying to use the Gorgias Chat API before its initialization
       (property or function "isOpen")!"). Every read of it, typeof included, stays inside a try. */
    const isOpen = () => {
      try { return window.GorgiasChat.isOpen() === true } catch (error) { return false }
    }
    const openChat = () => {
      try { window.GorgiasChat.open() } catch (error) {}
    }
    /* The embed's own loader has run once GorgiasChat exists: the pre-init proxy counts. */
    const hasApi = () => {
      try { return !!window.GorgiasChat } catch (error) { return false }
    }

    /* Gorgias's documented start: GorgiasChat.init() once GorgiasChat exists, or their own
       gorgias-widget-loaded event, whichever settles first. GorgiasChat exists within a few ms of the
       loader's load event and the event fires about 0.4 s later (measured), so init() is tried from
       that load event and from a poll. It runs once; if it throws, it is tried again a moment later.
       init() on a widget that has already loaded resolves at once (measured). */
    const whenReady = () => new Promise(resolve => {
      let settled = false
      let initialised = false
      let threw = false
      const finish = () => { if (!settled) { settled = true; resolve() } }
      const limit = Date.now() + WAIT_CAP
      window.addEventListener('gorgias-widget-loaded', finish, { once: true })
      startChat = () => {
        if (settled || initialised) return true
        let api = null
        try { api = window.GorgiasChat || null } catch (error) {}
        if (!api) return false
        try {
          const pending = api.init()
          initialised = true
          if (pending && typeof pending.then === 'function') pending.then(finish, finish)
          else finish()
        } catch (error) { threw = true }
        return initialised
      }
      const look = () => {
        if (startChat() || broken || Date.now() > limit) return
        setTimeout(look, threw ? 250 : 50)
      }
      look()
    })

    /* Follow the tapped launcher's own link. Another page replaces this one, so the spinner keeps
       turning until it does (and stops if the shopper comes back to this page out of the back cache);
       a mailto: link or a same-page address leaves this page standing, so the spinner stops now. */
    const leave = (el) => {
      let away = false
      try {
        away = /^https?:$/.test(el.protocol) &&
          !(el.origin === location.origin && el.pathname === location.pathname && el.search === location.search)
      } catch (error) {}
      if (away) window.addEventListener('pageshow', () => el.removeAttribute('aria-busy'), { once: true })
      else el.removeAttribute('aria-busy')
      try { window.location.assign(el.href) } catch (error) { el.removeAttribute('aria-busy') }
    }

    /* Someone asked for the chat — a tap, or a link carrying gs_open_chat. The spinner turns until
       isOpen() says the chat really opened, and only then does our bubble step aside. Measured on a
       throttled phone (Slow 3G) the loader arrived at 2.2 s and the chat opened at 16.5 s, so the wait
       has two parts. Until the loader has run, a tap follows the bubble's own link if the loader fails
       (at once) or has not arrived eight seconds after the tap. Once it has run, the tap waits up to
       thirty seconds for the chat and never navigates; after that the spinner stops and the next tap
       follows the link. And nothing navigates on its own once the shopper has scrolled, tapped
       something else or left the page since the tap: the spinner stops instead, and the next tap
       follows the link. A link never navigates by itself at all. Only the launcher asked spins; a
       second tap while one waits, on any launcher, joins that wait. */
    const ask = (tapped, el) => {
      if (request) {
        /* a tap while a gs_open_chat link is still loading is a tap: the rules count from it */
        if (tapped && !request.tapped) { request.tapped = true; request.at = Date.now(); request.moved = false }
        /* the spinner moves to the launcher just tapped */
        if (tapped && el !== request.el) { request.el.removeAttribute('aria-busy'); request.el = el; el.setAttribute('aria-busy', 'true') }
        return
      }
      const mine = request = { tapped, at: Date.now(), moved: false, el }
      el.setAttribute('aria-busy', 'true')
      const moved = (event) => {
        if (event.type === 'pointerdown' && event.target && event.target.closest && event.target.closest('[data-chat-launcher]')) return
        if (event.type === 'visibilitychange' && document.visibilityState !== 'hidden') return
        mine.moved = true
      }
      window.addEventListener('scroll', moved, { capture: true, passive: true })
      document.addEventListener('pointerdown', moved, true)
      document.addEventListener('visibilitychange', moved)
      const end = () => {
        request = null
        window.removeEventListener('scroll', moved, { capture: true })
        document.removeEventListener('pointerdown', moved, true)
        document.removeEventListener('visibilitychange', moved)
      }
      let nudged = 0
      if (ready) ready.then(() => { if (request === mine) { nudged = Date.now(); openChat() } })
      const look = () => {
        if (request !== mine) return
        /* the embed's loader is not ours, so its load event never reaches us: GorgiasChat existing says it ran */
        if (!loaded && embedded && hasApi()) loaded = true
        if (isOpen()) {
          end()
          mine.el.removeAttribute('aria-busy')
          /* their launcher is up now: the floating bubble steps aside; a section's button stays where it is */
          if (bubble) bubble.hidden = true
          return
        }
        const since = Date.now() - mine.at
        if (broken || (!loaded && since > LOADER_CAP)) {
          end()
          if (mine.tapped) broken = true
          if (mine.tapped && !mine.moved) return leave(mine.el)
          mine.el.removeAttribute('aria-busy')
          return
        }
        if (loaded && since > OPEN_CAP) {
          end()
          if (mine.tapped) broken = true
          mine.el.removeAttribute('aria-busy')
          return
        }
        /* open() called before their window was up: ask once more, a second later */
        if (nudged && Date.now() - nudged > 1000) { nudged = Date.now(); openChat() }
        setTimeout(look, 100)
      }
      look()
    }

    /* After a load nobody is waiting on any more (the merchant's delay, or a tap the shopper walked
       away from), Gorgias's own button appears once the chat is ready. When it is on screen and a
       finger at its centre reaches it, it is the one launcher and ours steps aside: with the delay on
       and the privacy banner up, ours sat lifted mid-screen while theirs was drawn over the banner
       (measured). On a draft preview Shopify's bar covers theirs, so ours stays. */
    const theirsShown = () => {
      try {
        const button = document.querySelector('iframe[title="Gorgias live chat messenger"]')
        if (!button) return false
        const style = getComputedStyle(button)
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false
        const box = button.getBoundingClientRect()
        const x = box.left + box.width / 2, y = box.top + box.height / 2
        if (box.width < 8 || box.height < 8 || x < 0 || y < 0 || x > innerWidth || y > innerHeight) return false
        return document.elementFromPoint(x, y) === button
      } catch (error) { return false }
    }
    const yieldToTheirs = () => {
      if (!bubble) return
      const until = Date.now() + 10000
      const look = () => {
        if (bubble.hidden) return
        if (!request && theirsShown()) { bubble.hidden = true; return }
        if (Date.now() < until) setTimeout(look, 250)
      }
      look()
    }

    const load = (open, tapped, el) => {
      if (!started) {
        started = true
        const script = tag(CHAT_SRC, 'data-gorgias-loader-chat')
        if (CONVERT_SRC) tag(CONVERT_SRC, 'data-gorgias-loader-convert')
        ready = whenReady()
        if (script) {
          script.addEventListener('load', () => { loaded = true; startChat() }, { once: true })
          script.addEventListener('error', () => { broken = true }, { once: true })
        }
        ready.then(yieldToTheirs)
      }
      if (open) ask(tapped, el || lead)
    }

    /* A click, never touchend: a swipe that starts on the bubble (which the banner lift puts mid-screen)
       fired touchend, and scrolled 395 px AND opened the full-screen chat (measured). A real tap still
       ends in a click, 5 ms after the finger lifts. */
    /* One listener on the document serves every launcher, including a chat section the theme editor
       adds after the page has loaded. It listens in the capture phase, so another script's link
       handler further down cannot send the tap away first. A hidden bubble cannot be tapped. A click
       with a modifier key (a new tab) is left to the browser. */
    const onTap = (event) => {
      const el = event.target && event.target.closest ? event.target.closest('[data-chat-launcher]') : null
      if (!el || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (broken) return
      event.preventDefault()
      load(true, true, el)
    }
    document.addEventListener('click', onTap, true)

    /* Shopify's privacy banner must never sit on the bubble (see clearOfBanner above). */
    if (bubble && !bubble.hidden) try { clearOfBanner(bubble) } catch (error) {}
    if (embedded) return

    /* A link the merchant hands out: open the chat on arrival. */
    let asked = false
    try { asked = new URLSearchParams(location.search).has('gs_open_chat') } catch (error) {}
    if (asked) return load(true, false)

    /* The merchant's optional second chance: after N seconds the shopper actually spent here,
       fetch the chat in idle time so the badge and the campaigns can appear. Never opens it. */
    if (DELAY > 0) {
      let spent = 0
      const idle = (fn) => {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 2000 })
        else fn()
      }
      const tick = () => {
        if (started) return
        if (document.visibilityState === 'visible') spent += 1
        if (spent >= DELAY) return idle(() => load(false))
        setTimeout(tick, 1000)
      }
      setTimeout(tick, 1000)
    }
  }

  if (document.prerendering) document.addEventListener('prerenderingchange', boot, { once: true })
  else boot()
})()
